import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';


const API_URL =  'https://audit-ai.net/api'  

//const API_URL = 'http://localhost:8000/api'

const AuthService = {
  // Common cookie options for auth data
  cookieOptions: {
    expires: 1,          // 1 day expiration
    secure: true,        // Only sent over HTTPS
    sameSite: 'strict', // Protect against CSRF
    path: '/'           // Available across the app
  },

  login: async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/login/`, { username, password });
      
      if (response.data.access && response.data.refresh) {
        const { access, refresh, groups, audit_role } = response.data;
        
        // Store tokens in localStorage
        localStorage.setItem('token', access);
        localStorage.setItem('refresh_token', refresh);
        localStorage.setItem('username', username);

        // Store all auth data in cookies with consistent security options
        Cookies.set('token', access, AuthService.cookieOptions);
        Cookies.set('refresh_token', refresh, { ...AuthService.cookieOptions, expires: 7 }); // Refresh token lives longer
        Cookies.set('username', username, AuthService.cookieOptions);
        
        if (groups) {
          Cookies.set('groups', JSON.stringify(groups), AuthService.cookieOptions);
        }
        
        if (audit_role) {
          Cookies.set('audit_role', audit_role, AuthService.cookieOptions);
        }
      }
      
      return response.data;
    } catch (error) {
      throw error; // Allow calling component to handle login failure with detailed error
    }
  },

  logout: async () => {
    try {
      // Get username to send to server for token invalidation
      const username = AuthService.getUsername();
      
      if (username) {
        // Call API to invalidate the token on server
        await axios.post(`${API_URL}/logout/`, { email: username });
      }
    } catch (error) {
      console.error('Error invalidating token on server:', error);
    } finally {
      // Always clear client-side storage
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('username');
      

      // Clear all auth-related cookies
      const cookiesToRemove = ['token', 'refresh_token', 'csrftoken', 'groups', 'audit_role', 'username'];
      cookiesToRemove.forEach(cookie => {
        Cookies.remove(cookie, { path: '/' });
      });

      // Redirect to login form instead of login
      window.location.href = '/loginform';
    }
  },

  getToken: () => {
    return localStorage.getItem('token') || Cookies.get('token'); // Retrieve access token from localStorage or cookies
  },

  getRefreshToken: () => {
    return localStorage.getItem('refresh_token') || Cookies.get('refresh_token'); // Retrieve refresh token from localStorage or cookies
  },

  getUsername: () => {
    return localStorage.getItem('username') || Cookies.get('username'); // Retrieve username from localStorage or cookies
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('token') || Cookies.get('token');
    if (!token) return false;
    
    // Check if token is expired
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // Return true only if token exists and is not expired
      return decodedToken.exp > currentTime;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  },

  refreshToken: async () => {
    const refresh_token = AuthService.getRefreshToken();
    try {
      const response = await axios.post(`${API_URL}/token/refresh/`, { refresh: refresh_token });
      const newAccessToken = response.data.access;
      
      // Update access token in both localStorage and cookies with consistent options
      localStorage.setItem('token', newAccessToken);
      Cookies.set('token', newAccessToken, AuthService.cookieOptions);
      
      return newAccessToken;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw error;
    }
  },

  resetPassword: async (email, tempPassword, newPassword) => {
    try {
      const response = await axios.post(`${API_URL}/password/reset/`, {
        email,
        temp_password: tempPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default AuthService;
